import type {
  OnBeforeUploadContinueResult,
  OnBeforeUploadErrorResult,
  OnBeforeUploadResult,
} from 'hooks/useUpload/types';
import type { I18nFn } from 'i18n/types/I18nFn';

type BeforeUploadStep = (f: File) => Promise<OnBeforeUploadResult>;

export async function runBeforeUploadSteps(
  checks: BeforeUploadStep[],
  file: File,
): Promise<OnBeforeUploadResult> {
  let result: OnBeforeUploadResult = { type: 'continue', file };

  for (const check of checks) {
    // eslint-disable-next-line no-await-in-loop
    result = await check(result.file);
    if (result.type === 'cancel') return result;
    if (result.type === 'error') return result;
  }

  return result;
}

function validateImageDimensions(i18n: I18nFn, fsFile: File) {
  const url = URL.createObjectURL(fsFile);
  const img = new Image();

  img.onload = (event) => {
    const element = event.target as HTMLImageElement;
    URL.revokeObjectURL(url);
    if (element.naturalHeight > 10000 || element.naturalWidth > 10000) {
      throw new Error(i18n('Image dimensions must be within 10000px'));
    }
  };
  img.src = url;
  return fsFile;
}

// eslint-disable-next-line @typescript-eslint/require-await
export async function checkImageDimensions(i18n: I18nFn, file: File) {
  try {
    const validatedFile = validateImageDimensions(i18n, file);
    return {
      type: 'continue',
      file: validatedFile,
    } satisfies OnBeforeUploadContinueResult;
  } catch (resizeValidationError) {
    return {
      type: 'error',
      error:
        resizeValidationError instanceof Error
          ? resizeValidationError
          : new Error(String(resizeValidationError)),
    } satisfies OnBeforeUploadErrorResult;
  }
}
